import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getUserById, getAllTasks, getIdeasByUserId } from "../../Utils/apiServices";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import { Typography } from "../../Components/Typography";
import Avatar from "../../Components/Avatar";
import Table from "../../Components/Table";
import CompleteRegistration from "../CompleteRegistration";
import { extractIdeasData } from "../IdeasReview/utils";

// Dynamic field config: API key → { label, section, type? }
// Labels and sections drive the admin view; add new API keys here to show them.
const FIELD_CONFIG = {
  id: { label: "ID", section: "system" },
  email: { label: "Email", section: "personal" },
  secondary_email: { label: "Secondary Email", section: "personal" },
  username: { label: "Username", section: "personal" },
  phone: { label: "Phone", section: "personal" },
  role: { label: "Role", section: "personal" },
  first_name: { label: "First Name", section: "personal" },
  last_name: { label: "Last Name", section: "personal" },
  profession: { label: "Profession", section: "personal" },
  organization: { label: "Organization", section: "personal" },
  experience_years: { label: "Experience Years", section: "professional" },
  years_of_experience: { label: "Years of Experience", section: "professional" },
  industry: { label: "Industry", section: "professional" },
  functional_experience: { label: "Functional Experience", section: "professional" },
  technology_stack: { label: "Technology Stack", section: "professional" },
  specialization: { label: "Specialization", section: "professional" },
  primary_stream: { label: "Primary Stream", section: "professional" },
  college: { label: "College", section: "professional" },
  collage: { label: "Institution (College/University)", section: "professional" },
  year: { label: "Year of Graduation", section: "professional" },
  area_of_interest: { label: "Area of Interest", section: "professional" },
  skills: { label: "Skills", section: "professional" },
  expertise: { label: "Expertise", section: "professional" },
  interests: { label: "Interests", section: "professional" },
  maximum_engagements_assigned: { label: "Maximum Engagements Assigned", section: "professional" },
  linkedin_url: { label: "LinkedIn URL", section: "social", type: "link" },
  x_url: { label: "X (Twitter) URL", section: "social", type: "link" },
  github_url: { label: "GitHub URL", section: "social", type: "link" },
  facebook_url: { label: "Facebook URL", section: "social", type: "link" },
  calendly_link: { label: "Calendly Link", section: "social", type: "link" },
  preferred_commitment: { label: "Preferred Commitment", section: "professional" },
  country: { label: "Country", section: "location" },
  state: { label: "State", section: "location" },
  city: { label: "City", section: "location" },
  timezone: { label: "Timezone", section: "location" },
  bio: { label: "Bio", section: "profile" },
  tagline: { label: "Tagline", section: "profile" },
  profile_picture_url: { label: "Profile Picture URL", section: "profile", type: "link" },
  mentor_status: { label: "Mentor Status", section: "volunteer" },
  activities_to_volunteer_for: { label: "Activities to Volunteer For", section: "volunteer" },
  availability_and_motivation: { label: "Availability and Motivation", section: "volunteer" },
  innovation_excites_you: { label: "Innovation Excites You", section: "volunteer" },
  success: { label: "Success", section: "volunteer" },
  achievement: { label: "Achievement", section: "volunteer" },
  is_active: { label: "Is Active", section: "system", type: "boolean" },
  is_profile_completed: { label: "Profile Completed", section: "system", type: "boolean" },
  public_visibility: { label: "Public Visibility", section: "system", type: "boolean" },
  account_id: { label: "Account ID", section: "system" },
  created_at: { label: "Created At", section: "system", type: "date" },
  updated_at: { label: "Updated At", section: "system", type: "date" },
};

const SECTION_META = {
  personal: { title: "Personal Information", icon: "lucide:user" },
  professional: { title: "Professional Information", icon: "lucide:briefcase" },
  location: { title: "Location", icon: "lucide:map-pin" },
  profile: { title: "Profile & Bio", icon: "lucide:file-text" },
  social: { title: "Social & Links", icon: "lucide:share-2" },
  volunteer: { title: "Volunteer / Mentor", icon: "lucide:heart-handshake" },
  system: { title: "System Information", icon: "lucide:settings" },
  other: { title: "Other", icon: "lucide:list" },
};

const SECTION_ORDER = ["personal", "professional", "location", "profile", "social", "volunteer", "system", "other"];

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const accountContactFromList = location.state?.accountContactFromList ?? null;
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [studentIdeaLinks, setStudentIdeaLinks] = useState([]);
  const [studentIdeasLoading, setStudentIdeasLoading] = useState(false);

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserById(id);
      if (response.success && response.data?.data) {
        const userData = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;
        setUser(userData);

        if (userData?.role === "student" && id) {
          try {
            setStudentIdeasLoading(true);
            const ideasRes = await getIdeasByUserId(id);
            setStudentIdeaLinks(extractIdeasData(ideasRes));
          } catch (ideasErr) {
            console.error("Error fetching ideas for student:", ideasErr);
            setStudentIdeaLinks([]);
          } finally {
            setStudentIdeasLoading(false);
          }
        } else {
          setStudentIdeaLinks([]);
        }
      } else {
        setError("User not found");
        setStudentIdeaLinks([]);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError(err.error || "Failed to load user details");
      setStudentIdeaLinks([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUserTasks = useCallback(async () => {
    if (!id) return;
    try {
      setTasksLoading(true);
      const response = await getAllTasks({ client_id: id });
      if (response.success && response.data?.data) {
        setTasks(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (err) {
      console.error("Error fetching user tasks:", err);
    } finally {
      setTasksLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUserDetails();
    fetchUserTasks();
  }, [fetchUserDetails, fetchUserTasks]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getEventName = (eventId) => {
    if (!eventId) return "—";
    return `Event #${eventId}`;
  };

  const formatDisplayValue = (value, type) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (type === "date") return formatDate(value);
    return String(value);
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      student: "Student",
      mentor: "Mentor",
      sponsor: "Sponsor",
      ecosystem_partner: "Ecosystem Partner",
      corporate_sponsor: "Corporate Sponsor",
      donor: "Donor",
      investor: "Investor",
      volunteer: "Volunteer",
      admin: "Admin",
      program_team: "Program Team",
    };
    return roleLabels[role] || (role ? String(role).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");
  };

  // Build dynamic fields from API response: only keys present in user, with labels from config or derived
  const getLabelFromKey = (key) => {
    if (FIELD_CONFIG[key]) return FIELD_CONFIG[key].label;
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const studentIdeasTableData = useMemo(() => {
    return studentIdeaLinks.map((item) => {
      const idea = item.idea || {};
      const resourceUrl =
        idea.idea_video_link || idea.other_document || idea.pitch_video_link || null;
      return {
        key: String(item.id != null ? item.id : idea.id != null ? idea.id : ""),
        ideaId: idea.id ?? "—",
        title: idea.title ?? "—",
        category: idea.innovation_category ?? "—",
        problemStatement: idea.problem_statement ?? "",
        primaryImpact: idea.primary_impact ?? "—",
        status: idea.status ?? "—",
        relationshipRole: item.role ?? "—",
        createdAt: idea.created_at ?? null,
        resourceUrl,
      };
    });
  }, [studentIdeaLinks]);

  const studentIdeasColumns = useMemo(
    () => [
      { header: "Idea ID", accessor: "ideaId", key: "ideaId" },
      { header: "Title", accessor: "title", key: "title" },
      {
        header: "Innovation category",
        accessor: "category",
        key: "category",
        render: (v) => (
          <Typography
            as="span"
            className="text-sm line-clamp-2 max-w-[260px]"
            title={v && v !== "—" ? String(v) : undefined}
          >
            {v && v !== "—" ? v : "—"}
          </Typography>
        ),
      },
      {
        header: "Problem statement",
        accessor: "problemStatement",
        key: "problemStatement",
        render: (v) => (
          <Typography
            as="span"
            className="text-sm line-clamp-2 max-w-[300px]"
            title={v ? String(v) : undefined}
          >
            {v ? v : "—"}
          </Typography>
        ),
      },
      { header: "Primary impact", accessor: "primaryImpact", key: "primaryImpact" },
      { header: "Status", accessor: "status", key: "status" },
      { header: "Idea–user role", accessor: "relationshipRole", key: "relationshipRole" },
      {
        header: "Idea created",
        accessor: "createdAt",
        key: "createdAt",
        type: "date",
        dateFormat: "short",
      },
      {
        header: "Actions",
        accessor: "ideaId",
        key: "actions",
        render: (ideaId, record) => (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/ideas/${ideaId}`)}
              className="text-sm text-purple-600 font-medium hover:underline"
            >
              View Idea
            </button>
            {record.resourceUrl && (
              <a
                href={record.resourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-blue hover:underline"
              >
                Doc
              </a>
            )}
          </div>
        ),
      },
    ],
    [navigate]
  );

  const fieldsBySection = useMemo(() => {
    if (!user) return {};
    const sections = {};
    const seen = new Set();
    Object.keys(user).forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      const config = FIELD_CONFIG[key] || { section: "other", label: getLabelFromKey(key) };
      const section = config.section in SECTION_META ? config.section : "other";
      if (!sections[section]) sections[section] = [];
      const rawValue = key === "role" ? getRoleLabel(user[key]) : user[key];
      sections[section].push({
        key,
        label: config.label || getLabelFromKey(key),
        value: rawValue,
        type: config.type,
      });
    });
    return sections;
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading user details...
        </Typography>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Typography as="p" className="text-red-600">
          {error || "User not found"}
        </Typography>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  // For ecosystem_partner, corporate_sponsor, investor: show the same registration form as edit view
  // Uses getAccountContact API to load account + contact data; admin can edit all fields
  const isPartnerInvestor =
    user?.role === "ecosystem_partner" || user?.role === "corporate_sponsor" || user?.role === "investor";
  if (isPartnerInvestor) {
    return (
      <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
        <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate(-1)}
            startIcon={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
          >
            Back
          </Button>
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Edit {user?.role === "corporate_sponsor" ? "Corporate Sponsor" : user?.role === "ecosystem_partner" ? "Ecosystem Partner" : "Investor"}
          </Typography>
        </div>
        <CompleteRegistration
          targetUser={user}
          isAdminEdit
          accountContactFromList={accountContactFromList}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
      {/* Header */}
      <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate(-1)}
            startIcon={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
          >
            Back
          </Button>
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            User Details
          </Typography>
        </div>
      </div>

      {/* Profile Picture and Basic Info */}
      <Card className="w-full shadow-sm border border-slate-200">
        <div className="p-8">
          <div className="flex items-start gap-8">
            <Avatar
              src={user.profile_picture_url}
              firstName={user.first_name}
              lastName={user.last_name}
              email={user.email}
              alt={user.first_name || user.email || "User"}
              size="w-32 h-32"
              className="border-4 border-white shadow-lg"
            />
            <div className="flex-1 pt-2">
              <div className="flex items-center gap-3 mb-3">
                <Typography as="h2" className="text-2xl font-bold text-primary-900">
                  {user.first_name || user.last_name
                    ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                    : user.email || "User"}
                </Typography>
                {user.is_active !== undefined && (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      user.is_active
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Icon icon="lucide:mail" className="h-4 w-4 text-tertiary-600" />
                <Typography as="p" className="text-sm text-tertiary-600">
                  {user.email}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <Icon icon="lucide:user-circle" className="h-4 w-4 text-tertiary-600" />
                <Typography as="p" className="text-sm font-medium text-primary-700">
                  {getRoleLabel(user.role)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Student ideas (GET /idea-user/:userId) — directly below profile card */}
      {user.role === "student" && (
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:lightbulb" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Ideas ({studentIdeasTableData.length})
              </Typography>
            </div>
            {studentIdeasLoading ? (
              <div className="flex items-center justify-center py-8">
                <Typography as="p" className="text-tertiary-600">
                  Loading ideas...
                </Typography>
              </div>
            ) : studentIdeasTableData.length === 0 ? (
              <div className="text-center py-12">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Icon icon="lucide:lightbulb-off" className="h-8 w-8 text-slate-400" />
                </div>
                <Typography as="p" className="text-tertiary-600 text-base">
                  No ideas linked to this student yet.
                </Typography>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table
                  columns={studentIdeasColumns}
                  data={studentIdeasTableData}
                  rowKey="key"
                  emptyMessage="No ideas linked to this student yet."
                  className="min-w-[900px]"
                />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* User Details Grid – all values from API, labels from FIELD_CONFIG */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {SECTION_ORDER.filter((sectionKey) => fieldsBySection[sectionKey]?.length > 0).map((sectionKey) => {
          const meta = SECTION_META[sectionKey] || SECTION_META.other;
          const fields = fieldsBySection[sectionKey];
          return (
            <Card key={sectionKey} className="w-full shadow-sm border border-slate-200">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
                  <Icon icon={meta.icon} className="h-5 w-5 text-purple-600" />
                  <Typography as="h3" className="text-lg font-semibold text-primary-900">
                    {meta.title}
                  </Typography>
                </div>
                <div className="space-y-5">
                  {fields.map((field) => {
                    const displayVal = formatDisplayValue(field.value, field.type);
                    const isEmpty = displayVal === "—";
                    return (
                      <div
                        key={field.key}
                        className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0"
                      >
                        <Typography
                          as="label"
                          className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider"
                        >
                          {field.label}
                        </Typography>
                        <Typography as="p" className="text-sm font-medium text-primary-900 break-words">
                          {field.type === "link" && field.value && field.value !== "" ? (
                            <a
                              href={field.value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-blue hover:underline inline-flex items-center gap-1"
                            >
                              {field.value}
                              <Icon icon="lucide:external-link" className="h-3 w-3 shrink-0" />
                            </a>
                          ) : field.type === "boolean" ? (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                field.value ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                              }`}
                            >
                              {displayVal}
                            </span>
                          ) : (
                            <span className={isEmpty ? "text-tertiary-500 italic" : ""}>
                              {displayVal}
                            </span>
                          )}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tasks Section */}
      <Card className="w-full shadow-sm border border-slate-200">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
            <Icon icon="lucide:check-square" className="h-5 w-5 text-purple-600" />
            <Typography as="h3" className="text-lg font-semibold text-primary-900">
              Assigned Tasks ({tasks.length})
            </Typography>
          </div>
          
          {tasksLoading ? (
            <div className="flex items-center justify-center py-8">
              <Typography as="p" className="text-tertiary-600">
                Loading tasks...
              </Typography>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Icon icon="lucide:check-square" className="h-8 w-8 text-slate-400" />
              </div>
              <Typography as="p" className="text-tertiary-600 text-base">
                No tasks assigned to this user.
              </Typography>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Serial #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Task Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {tasks.map((task) => (
                    <tr 
                      key={task.id} 
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm">
                          {task.serial_number || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Typography
                          as="span"
                          className="text-sm font-medium text-primary-900 line-clamp-2"
                          title={task.task_details}
                        >
                          {task.task_details || "-"}
                        </Typography>
                      </td>
                      <td className="px-6 py-4">
                        {task.event ? (
                          <div className="flex items-center gap-2">
                            <Icon icon="lucide:calendar" className="h-4 w-4 text-slate-400" />
                            <button
                              onClick={() => navigate(`/events/${task.event.id}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              {task.event.title || `Event #${task.event_id}`}
                            </button>
                          </div>
                        ) : (
                          <Typography as="span" className="text-sm text-tertiary-500">
                            {getEventName(task.event_id)}
                          </Typography>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          <Icon icon="lucide:clock" className="h-3 w-3 mr-1" />
                          {task.number_of_hours ? `${task.number_of_hours} hrs` : "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Icon icon="lucide:calendar" className="h-4 w-4 text-slate-400" />
                          <Typography as="span" className="text-sm font-medium text-primary-900">
                            {formatDateOnly(task.start_date)}
                          </Typography>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Icon icon="lucide:calendar-check" className="h-4 w-4 text-slate-400" />
                          <Typography as="span" className="text-sm font-medium text-primary-900">
                            {formatDateOnly(task.end_date)}
                          </Typography>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            task.is_active !== false
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {task.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserDetail;

